import { MotifTable } from "@ey-xd/motif-react";
import "./Table.scss";
import { useEffect, useMemo, useRef, useState } from "react";

export function Table({
  rowData,
  colDefs,
  pagesize = 10,
  rowClassRules,
  selectionChanged,
  rowClicked,
  forceUpdate = crypto.randomUUID(),
  rowDragged,
  rowSelect,
  draggable,
  loading,
  pagination = true,
  suppressMovableColumns,
  showVerticalScroll,
  onGridReady = () => {},
  onPaginationChanged = () => {},
  rowHeight = 72,
  headerHeight = 72,
  totalRecords,
  currentPage,
  onPageChange,
  onPageSizeChange,
  noRowsMessage = "No records found",
}) {
  const [gridApi, setGridApi] = useState();

  const isServerPagination = totalRecords != null && onPageChange != null;

  const expectedPageRef = useRef(currentPage || 1);
  const expectedSizeRef = useRef(pagesize);

  useEffect(() => {
    expectedPageRef.current = currentPage || 1;
  }, [currentPage]);

  useEffect(() => {
    expectedSizeRef.current = pagesize;
  }, [pagesize]);

  const effectiveRowData = useMemo(() => {
    if (!isServerPagination) return rowData;
    const total = totalRecords || 0;
    if (total === 0) return [];
    const arr = new Array(total).fill(null).map(() => ({}));
    const startIdx = ((currentPage || 1) - 1) * pagesize;
    if (rowData) {
      rowData.forEach((row, i) => {
        if (startIdx + i < arr.length) {
          arr[startIdx + i] = row;
        }
      });
    }
    return arr;
  }, [isServerPagination, totalRecords, currentPage, pagesize, rowData]);

  const isPlaceholderRow = (data) =>
    isServerPagination &&
    data &&
    Object.keys(data).length === 0 &&
    data.constructor === Object;

  const keepCurrentPageRowsVisibleAfterSort = (params) => {
    if (!isServerPagination) return;

    const startIdx =
      ((expectedPageRef.current || 1) - 1) * expectedSizeRef.current;
    const realNodes = [];
    const placeholderNodes = [];

    params.nodes.forEach((node) => {
      if (isPlaceholderRow(node.data)) {
        placeholderNodes.push(node);
      } else {
        realNodes.push(node);
      }
    });

    const beforePage = placeholderNodes.splice(0, startIdx);
    const reorderedNodes = [...beforePage, ...realNodes, ...placeholderNodes];

    params.nodes.splice(0, params.nodes.length, ...reorderedNodes);
  };

  const gridOptions = {
    overlayNoRowsTemplate: `<div class='app-table-no-rows'>${noRowsMessage}</div>`,
    postSortRows: keepCurrentPageRowsVisibleAfterSort,
  };

  useEffect(() => {
    if (gridApi && effectiveRowData && effectiveRowData.length > 0) {
      gridApi.setGridOption("rowData", effectiveRowData);
      if (isServerPagination) {
        const targetPage = (currentPage || 1) - 1;
        gridApi.paginationGoToPage(targetPage);
      }
      gridApi.redrawRows();
    }
  }, [currentPage, effectiveRowData, gridApi, forceUpdate, isServerPagination]);

  function handleGridReady(params) {
    setGridApi(params.api);
    onGridReady(params);
  }

  function handlePaginationChanged(params) {
    if (isServerPagination && params.api) {
      const agPage = params.api.paginationGetCurrentPage() + 1;
      const agSize = params.api.paginationGetPageSize();

      if (agSize !== expectedSizeRef.current && onPageSizeChange) {
        expectedSizeRef.current = agSize;
        expectedPageRef.current = 1;
        onPageSizeChange(agSize);
      } else if (agPage !== expectedPageRef.current) {
        expectedPageRef.current = agPage;
        onPageChange(agPage);
      }
    }
    onPaginationChanged(params);
  }

  return (
    <MotifTable
      suppressRowTransform={true}
      suppressRowHoverHighlight={true}
      suppressCellFocus
      suppressRowClickSelection
      rowDragEntireRow={draggable}
      suppressDragLeaveHidesColumns={true}
      rowDragManaged={true}
      suppressMoveWhenRowDragging={true}
      suppressMovableColumns={suppressMovableColumns || false}
      rowData={effectiveRowData}
      columnDefs={colDefs}
      pagination={pagination}
      onGridReady={handleGridReady}
      onPaginationChanged={handlePaginationChanged}
      paginationPageSize={pagesize}
      paginationPageSizeSelector={
        effectiveRowData && effectiveRowData.length > 0 ? [5, 10, 20] : [10]
      }
      onRowSelected={selectionChanged}
      onCellClicked={rowClicked}
      onRowDragEnd={rowDragged}
      rowHeight={rowHeight}
      rowSelection={rowSelect}
      editType={"fullRow"}
      headerHeight={headerHeight}
      gridOptions={gridOptions}
      rowClassRules={rowClassRules}
      domLayout="normal"
      loading={loading ? loading : false}
      overlayLoadingTemplate={`<div data-testid="table-loading" class="motif-progress-loader app-loading-indicator  app-loading-indicator-transparent app-table-no-rows" data-motif-progress-loader="true"><div class="motif-progress-loader-type"><div class="motif-progress-loader-content"><div class="motif-progress-loader-square"></div><div class="motif-progress-loader-square"></div><div class="motif-progress-loader-square"></div></div></div></div>`}
      firstColumnBorderRight
      tooltipShowDelay={0}
      tooltipHideDelay={3000}
    />
  );
}
